// File d'attente des pointages saisis hors réseau.
//
// Sur un chantier sans signal, la saisie est gardée DANS le téléphone, puis
// envoyée dès que le réseau revient. Ce sont des heures de paie : la règle est
// qu'une saisie ne disparaît jamais sans avoir été soit enregistrée sur le
// serveur, soit retirée par la personne elle-même.
//
// IndexedDB plutôt que localStorage : le contenu survit à la fermeture de l'app
// et à un manque de mémoire, là où localStorage peut être vidé par le système.
// Chaque saisie porte un identifiant fabriqué ici (clientBatchId) : c'est lui qui
// permet au serveur de reconnaître un envoi rejoué et de ne pas créer de doublon.

const BASE = 'planning-pointages';
const MAGASIN = 'file';
const VERSION = 1;

export const nouvelIdentifiant = () => (
  window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
);

const ouvrir = () => new Promise((resoudre, rejeter) => {
  const demande = window.indexedDB.open(BASE, VERSION);
  demande.onupgradeneeded = () => {
    const db = demande.result;
    if (!db.objectStoreNames.contains(MAGASIN)) {
      db.createObjectStore(MAGASIN, { keyPath: 'id' });
    }
  };
  demande.onsuccess = () => resoudre(demande.result);
  demande.onerror = () => rejeter(demande.error);
});

const transaction = async (mode, action) => {
  const db = await ouvrir();
  return new Promise((resoudre, rejeter) => {
    const tx = db.transaction(MAGASIN, mode);
    const magasin = tx.objectStore(MAGASIN);
    let resultat;
    try {
      resultat = action(magasin);
    } catch (erreur) {
      rejeter(erreur);
      return;
    }
    tx.oncomplete = () => { db.close(); resoudre(resultat?.result ?? resultat); };
    tx.onerror = () => { db.close(); rejeter(tx.error); };
    tx.onabort = () => { db.close(); rejeter(tx.error); };
  });
};

export const disponible = () => typeof window !== 'undefined' && !!window.indexedDB;

// Met une saisie de côté et rend l'enregistrement complet.
export const mettreEnAttente = async (payload) => {
  const saisie = {
    id: payload.clientBatchId || nouvelIdentifiant(),
    payload: { ...payload },
    queuedAt: new Date().toISOString(),
    essais: 0,
    refus: null,          // message du serveur quand il a refusé pour de bon
  };
  saisie.payload.clientBatchId = saisie.id;
  saisie.payload.queuedAt = saisie.queuedAt;
  await transaction('readwrite', (magasin) => magasin.put(saisie));
  return saisie;
};

export const lister = async () => {
  const tout = await transaction('readonly', (magasin) => magasin.getAll());
  return (tout || []).sort((a, b) => (a.queuedAt < b.queuedAt ? -1 : 1));
};

export const retirer = async (id) => transaction('readwrite', (magasin) => magasin.delete(id));

export const remplacer = async (saisie) => transaction('readwrite', (magasin) => magasin.put(saisie));

// Envoie ce qui attend, une saisie à la fois, en s'arrêtant à la première panne
// de réseau — inutile d'insister quinze fois si le signal n'est pas revenu.
// `envoyer` doit lever une erreur de type axios (erreur.response absent = réseau).
export const vider = async (envoyer) => {
  const bilan = { envoyees: 0, refusees: 0, restantes: 0, coupe: false };
  const attente = await lister();
  for (const saisie of attente) {
    if (saisie.refus) { bilan.refusees += 1; continue; }
    try {
      await envoyer(saisie.payload);
      await retirer(saisie.id);
      bilan.envoyees += 1;
    } catch (erreur) {
      const statut = erreur.response?.status;
      if (statut && statut >= 400 && statut < 500) {
        // Le serveur a compris et refuse : réessayer n'y changera rien. On garde
        // la saisie visible avec la raison, pour que personne ne la croie perdue.
        await remplacer({ ...saisie, essais: saisie.essais + 1,
                          refus: erreur.response?.data?.detail || 'Refusé par le serveur' });
        bilan.refusees += 1;
      } else {
        // Réseau absent ou serveur en panne : on réessaiera plus tard.
        await remplacer({ ...saisie, essais: saisie.essais + 1 });
        bilan.coupe = true;
        break;
      }
    }
  }
  bilan.restantes = (await lister()).length;
  return bilan;
};
